import type { ICreatedBy } from '@/modules/apiStore/types/adverse-events/ICreatedBy'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'

/**
 * Типы тем обращений граждан
 */
export type AppealTopic = 'review' | 'complaint' | 'wish'

/**
 * Типы статусов обращений граждан
 */
export type AppealStatus = 'new' | 'answered'

/**
 * Типы адресатов обращения
 */
export type AppealTargetType = 'department' | 'employee'

/**
 * Канал поступления обращения
 */
export interface IAppealChannel {
  id: number
  name: string
  is_system: boolean
  is_active: boolean
  created_at?: string
  created_by?: Partial<ICreatedBy> | null
}

/**
 * Редакция соглашения об обработке ПД, действовавшая на момент регистрации обращения
 */
export interface IAppealPdAgreementVersion {
  id: number
  number: number
  created_at: string
  effective_until: string | null
  original_filename: string
  file: string
}

/**
 * Статус блокировки модуля обращений граждан (`GET /api/appeals/module-status/`).
 * Модуль открыт, когда оба документа есть и синхронизированы с ЛК пациента:
 * загружаемое организацией соглашение и согласие, которое портал собирает из реквизитов в настройках организации.
 */
export interface IAppealModuleStatus {
  is_locked: boolean
  /** Соглашение об обработке ПД загружено */
  has_agreement: boolean
  /** Соглашение синхронизировано с ЛК */
  is_synced: boolean
  /** Согласие на обработку ПД сформировано из реквизитов организации */
  has_consent: boolean
  /** Согласие синхронизировано с ЛК */
  is_consent_synced: boolean
}

/**
 * Правовой документ модуля обращений (`GET /api/appeals/legal-documents/`)
 */
export interface IAppealLegalDocument {
  type: 'pd_agreement' | 'pd_consent'
  title: string
  version_number: number | null
  effective_from: string | null
  url: string | null
}

export interface IAppealFileItem {
  id: string
  original_name: string
  content_type: string
  size: number
  url: string
}

/**
 * Элемент списка обращений граждан
 */
export interface IAppealListItem {
  id: string
  local_id?: number | null
  external_id?: string | null
  number?: number
  channel?: IAppealChannel | null
  topic: AppealTopic | string
  status: AppealStatus | string
  created_at: string
  name?: string
  full_name?: string
  patient_name?: string
  author?: string
  email?: string | null
  phone?: string | null
  text?: string
  target_type?: AppealTargetType | string | null
  target_name?: string | null
  rating?: number | null
  reply_text?: string | null
  replied_at?: string | null
  replied_by?: Partial<ICreatedBy> | string | null
  coordinator?: Partial<ICreatedBy> | null
  created_by?: Partial<ICreatedBy> | null
  coordinator_entry?: IResponsibilityListItem | null
  subtasks?: IResponsibilityListItem[]
  editable_fields?: string[]
  requires_action?: boolean
  pd_agreement_version?: IAppealPdAgreementVersion | null
  /** Обращение пришло из ЛК пациента: поля заявителя закрыты для редактирования */
  is_lkpatient?: boolean
  files?: IAppealFileItem[]
}

/**
 * Детальная карточка обращения гражданина
 */
export interface IAppealDetail {
  id: string
  local_id?: number | null
  external_id?: string | null
  number?: number
  channel?: IAppealChannel | null
  topic: AppealTopic | string
  status: AppealStatus | string
  created_at: string
  name?: string
  full_name?: string
  patient_name?: string
  author?: string
  email?: string | null
  phone?: string | null
  text: string
  target_type?: AppealTargetType | string | null
  target_name?: string | null
  rating?: number | null
  reply_text?: string | null
  replied_at?: string | null
  replied_by?: Partial<ICreatedBy> | string | null
  coordinator?: Partial<ICreatedBy> | null
  created_by?: Partial<ICreatedBy> | null
  coordinator_entry?: IResponsibilityListItem | null
  subtasks?: IResponsibilityListItem[]
  editable_fields?: string[]
  requires_action?: boolean
  pd_agreement_version?: IAppealPdAgreementVersion | null
  /** Обращение пришло из ЛК пациента: поля заявителя закрыты для редактирования */
  is_lkpatient?: boolean
  hospital?: {
    slug?: string
    name?: string
  } | string | null
  files?: IAppealFileItem[]
}

/**
 * Маппинг меток для тем обращений
 */
export const APPEAL_TOPIC_LABELS: Record<AppealTopic | string, string> = {
  review: 'Отзыв',
  complaint: 'Жалоба',
  wish: 'Пожелание',
}

/**
 * Маппинг меток для статусов обращений
 */
export const APPEAL_STATUS_LABELS: Record<AppealStatus | string, string> = {
  new: 'Новое',
  answered: 'Отвечено',
}

/**
 * Маппинг меток для типа адресата
 */
export const APPEAL_TARGET_TYPE_LABELS: Record<AppealTargetType | string, string> = {
  department: 'Отделение',
  employee: 'Сотрудник',
}
